import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getApplications, getFilterOptions } from "@/lib/api";
import { useAppStore } from "@/store/appStore";
import { STREAM_COLORS, CRIT_COLORS, statusMeta, fmtDate, daysAgo } from "@/lib/format";
import { SectionCard, StatusChip, KpiCard, Sparkline, EmptyState, StreamBadges } from "@/components/tmo/primitives";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  Search, ArrowUpDown, ArrowUp, ArrowDown, Table2, Layers, X, Users, HardHat, Briefcase, Share2,
  Info, HelpCircle, ChevronLeft, ChevronRight, Boxes,
} from "lucide-react";

const STREAM_LABEL = { SUBSCRIBER: "Subscriber", TECHNICIAN: "Technician", EXECUTIVE: "Executive", SHARED: "Shared" };
const STREAM_META = [
  { key: "SUBSCRIBER", label: "Subscriber / Customer", icon: Users, desc: "Customer-facing channels: web, mobile, IVR, notifications." },
  { key: "TECHNICIAN", label: "Technician / Field", icon: HardHat, desc: "Field crew tools: dispatch, OMS/ADMS, damage assessment, gas ops." },
  { key: "EXECUTIVE", label: "Executive / Leadership", icon: Briefcase, desc: "Decision support: analytics, dashboards, reporting pipelines." },
  { key: "SHARED", label: "Shared / Infrastructure", icon: Share2, desc: "Platform & infrastructure serving every stream (network, EAI, identity)." },
];

const DROPDOWNS = [
  ["stream", "Stream"], ["criticality", "Criticality"], ["readiness", "Readiness"],
  ["sme", "SME"], ["director", "Director"], ["department", "Department"],
  ["business_capability", "Capability"], ["application_type", "Type"],
];

export default function Applications() {
  const scenario = useAppStore((s) => s.scenario);
  const { filters, setFilter, clearFilters, openExplain } = useAppStore();
  const [search, setSearch] = useState("");
  const [view, setView] = useState("table");
  const [sortBy, setSortBy] = useState("score");
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(1);
  const pageSize = 12;

  const { data, isLoading } = useQuery({
    queryKey: ["apps-all", scenario],
    queryFn: () => getApplications({ scenario, page_size: 200 }),
  });
  const { data: options } = useQuery({ queryKey: ["filter-options"], queryFn: getFilterOptions });

  const allRows = data?.rows || [];

  const filtered = useMemo(() => {
    let rows = allRows;
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter((r) => (r.name + r.business_capability + r.sme + r.director).toLowerCase().includes(q));
    }
    Object.entries(filters).forEach(([k, v]) => {
      if (k === "stream") rows = rows.filter((r) => r.streams.includes(v));
      else if (k === "readiness") rows = rows.filter((r) => r.status === v);
      else if (k === "__critical") rows = rows.filter((r) => r.criticality === "Very High" || r.criticality === "High");
      else if (k === "__overdue") rows = rows.filter((r) => r.last_test == null || daysAgo(r.last_test) > 120);
      else rows = rows.filter((r) => r[k] === v);
    });
    const sorted = [...rows].sort((a, b) => {
      const av = a[sortBy], bv = b[sortBy];
      if (av == null) return 1; if (bv == null) return -1;
      const cmp = typeof av === "number" ? av - bv : String(av).localeCompare(String(bv));
      return sortDir === "asc" ? cmp : -cmp;
    });
    return sorted;
  }, [allRows, search, filters, sortBy, sortDir]);

  const kpis = useMemo(() => {
    const scored = allRows.filter((r) => r.score != null);
    const avg = scored.length ? Math.round(scored.reduce((s, r) => s + r.score, 0) / scored.length) : 0;
    return {
      total: allRows.length,
      ready: allRows.filter((r) => r.status === "READY").length,
      at_risk: allRows.filter((r) => r.status === "AT_RISK").length,
      not_ready: allRows.filter((r) => r.status === "NOT_READY").length,
      avg,
      critical: allRows.filter((r) => r.criticality === "Very High" || r.criticality === "High").length,
      overdue: allRows.filter((r) => r.last_test == null || daysAgo(r.last_test) > 120).length,
    };
  }, [allRows]);

  const activeCount = Object.keys(filters).length + (search ? 1 : 0);
  const toggleSort = (col) => {
    if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(col); setSortDir("asc"); }
  };
  const SortIcon = ({ col }) => sortBy !== col ? <ArrowUpDown size={11} className="opacity-40" /> : sortDir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />;

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageRows = filtered.slice((page - 1) * pageSize, page * pageSize);
  React.useEffect(() => { setPage(1); }, [search, filters, sortBy, sortDir, view]);

  if (isLoading) return <div className="space-y-4"><div className="grid grid-cols-7 gap-3">{Array.from({ length: 7 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-lg" />)}</div><Skeleton className="h-96 rounded-lg" /></div>;

  const KPIS = [
    { label: "Total Applications", value: kpis.total, accent: "#0F2043", sub: "Across all streams", onClick: () => clearFilters() },
    { label: "Ready (Pass)", value: kpis.ready, accent: "#00A651", sub: `${kpis.total ? Math.round(kpis.ready / kpis.total * 100) : 0}%`, onClick: () => setFilter("readiness", "READY") },
    { label: "At Risk (Caution)", value: kpis.at_risk, accent: "#F5A623", sub: `${kpis.total ? Math.round(kpis.at_risk / kpis.total * 100) : 0}%`, onClick: () => setFilter("readiness", "AT_RISK") },
    { label: "Not Ready (Fail)", value: kpis.not_ready, accent: "#D0021B", sub: `${kpis.total ? Math.round(kpis.not_ready / kpis.total * 100) : 0}%`, onClick: () => setFilter("readiness", "NOT_READY") },
    { label: "Avg. Confidence", value: `${kpis.avg}%`, accent: "#0075C9", sub: "Weighted mean" },
    { label: "Critical Apps", value: kpis.critical, accent: "#7B4FA8", sub: "High / Very High", onClick: () => setFilter("__critical", true) },
    { label: "Overdue Tests", value: kpis.overdue, accent: "#D0021B", sub: "> 120 days", onClick: () => setFilter("__overdue", true) },
  ];

  return (
    <div className="space-y-4" data-testid="applications-page">
      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
        {KPIS.map((k) => <KpiCard key={k.label} {...k} testid={`kpi-${k.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} />)}
      </div>

      {/* Stream mapping explainer + counts */}
      <SectionCard title="Stream Mapping" subtitle="How each application is assigned to Subscriber, Technician or Executive">
        <div className="flex items-start gap-2 mb-3 text-xs text-cnp-textMuted bg-cnp-bg rounded-lg p-2.5">
          <Info size={15} className="text-cnp-blue shrink-0 mt-0.5" />
          <span>Assignments come from the workbook's <b>Subscriber / Technician / Leadership-facing</b> columns. Those columns are currently empty in the Phase-2 tracker, so mapping is derived from each application's role, with platform &amp; infrastructure marked <b>Shared</b>. An application can serve more than one stream.</span>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {STREAM_META.map((s) => {
            const count = allRows.filter((r) => r.streams.includes(s.key)).length;
            const Icon = s.icon;
            const active = filters.stream === s.key;
            return (
              <button key={s.key} data-testid={`stream-map-${s.key}`} onClick={() => setFilter("stream", active ? null : s.key)}
                className={`text-left rounded-lg border-2 p-3 transition-all hover:shadow-md ${active ? "shadow-md" : ""}`}
                style={{ borderColor: active ? STREAM_COLORS[s.key] : "#E3E8EF" }}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <div className="h-7 w-7 rounded-full flex items-center justify-center" style={{ backgroundColor: STREAM_COLORS[s.key] }}><Icon size={15} className="text-white" /></div>
                    <span className="text-xs font-semibold text-cnp-text">{s.label}</span>
                  </div>
                  <span className="text-2xl font-bold tnum" style={{ color: STREAM_COLORS[s.key] }}>{count}</span>
                </div>
                <p className="text-[10px] text-cnp-textMuted leading-tight">{s.desc}</p>
              </button>
            );
          })}
        </div>
      </SectionCard>

      {/* Filter bar + view toggle */}
      <div className="bg-cnp-card rounded-lg border border-cnp-border shadow-sm p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-cnp-textMuted" />
          <Input data-testid="app-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search applications by name, keyword..." className="pl-8 h-9" />
        </div>
        {DROPDOWNS.map(([key, label]) => (
          <Select key={key} value={filters[key] || "all"} onValueChange={(v) => setFilter(key, v)}>
            <SelectTrigger data-testid={`filter-${key}`} className="h-9 w-auto min-w-[110px] text-xs"><SelectValue placeholder={label} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{label}: All</SelectItem>
              {(key === "stream" ? ["SUBSCRIBER", "TECHNICIAN", "EXECUTIVE", "SHARED"] : options?.[key] || []).map((o) => (
                <SelectItem key={o} value={o}>{key === "stream" ? (STREAM_LABEL[o] || o) : o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ))}
        {activeCount > 0 && (
          <button data-testid="clear-filters" onClick={() => { clearFilters(); setSearch(""); }}
            className="h-9 flex items-center gap-1 rounded-md bg-cnp-bg px-2.5 text-xs font-semibold text-cnp-red hover:bg-cnp-red/10">
            <X size={13} /> Clear ({activeCount})
          </button>
        )}
        <div className="ml-auto flex items-center rounded-md border border-cnp-border overflow-hidden">
          {[["table", Table2, "Table"], ["stream", Layers, "By Stream"]].map(([v, Icon, label]) => (
            <button key={v} data-testid={`view-${v}`} onClick={() => setView(v)}
              className={`flex items-center gap-1.5 px-3 h-9 text-xs font-semibold ${view === v ? "bg-cnp-navy text-white" : "text-cnp-text hover:bg-cnp-bg"}`}>
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>
      </div>

      {/* Views */}
      {view === "table" ? (
        <SectionCard>
          {filtered.length === 0 ? <EmptyState icon={Boxes} title="No applications match your filters" hint="Try clearing a filter or changing the search." /> : (
          <>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="apps-table">
              <thead><tr className="text-cnp-textMuted text-left border-b border-cnp-border">
                {[["name", "Application"], ["streams", "Streams"], ["criticality", "Criticality"], ["business_capability", "Capability"],
                  ["application_type", "Type"], ["sme", "SME"], ["director", "Director"], ["last_test", "Last Test"],
                  ["status", "Status"], ["score", "Readiness"], ["high_priority_issues", "Issues"]].map(([col, label]) => (
                  <th key={col} className="pb-2 pr-3 font-medium cursor-pointer select-none" onClick={() => toggleSort(col)}>
                    <span className="inline-flex items-center gap-1">{label} <SortIcon col={col} /></span>
                  </th>
                ))}
              </tr></thead>
              <tbody>
                {pageRows.map((r) => (
                  <tr key={r.application_id} data-testid={`app-row-${r.application_id}`}
                    onClick={() => openExplain({ type: "application", id: r.application_id, name: r.name, title: r.name })}
                    className="border-b border-cnp-border/60 hover:bg-cnp-bg cursor-pointer">
                    <td className="py-2 pr-3 font-medium text-cnp-text max-w-[220px] truncate">{r.name}</td>
                    <td className="py-2 pr-3"><StreamBadges streams={r.streams} /></td>
                    <td className="py-2 pr-3"><span className="font-semibold" style={{ color: CRIT_COLORS[r.criticality] }}>{r.criticality}</span></td>
                    <td className="py-2 pr-3 text-cnp-textMuted">{r.business_capability}</td>
                    <td className="py-2 pr-3 text-cnp-textMuted">{r.application_type}</td>
                    <td className="py-2 pr-3 text-cnp-textMuted">{r.sme}</td>
                    <td className="py-2 pr-3 text-cnp-textMuted">{r.director}</td>
                    <td className="py-2 pr-3 tnum">{fmtDate(r.last_test)}</td>
                    <td className="py-2 pr-3"><StatusChip status={r.status} /></td>
                    <td className="py-2 pr-3"><div className="flex items-center gap-2"><span className="tnum font-semibold w-9">{r.score == null ? "N/A" : `${r.score}%`}</span><div className="h-1.5 w-14 rounded-full bg-cnp-bg overflow-hidden"><div className="h-full rounded-full" style={{ width: `${r.score || 0}%`, backgroundColor: statusMeta(r.status).color }} /></div></div></td>
                    <td className="py-2 pr-3"><span className={`tnum font-semibold ${r.high_priority_issues > 0 ? "text-cnp-red" : "text-cnp-textMuted"}`}>{r.high_priority_issues}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between mt-3 text-xs text-cnp-textMuted">
            <span>Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, filtered.length)} of {filtered.length}</span>
            <div className="flex items-center gap-1">
              <button disabled={page === 1} onClick={() => setPage((p) => p - 1)} className="p-1.5 rounded border border-cnp-border disabled:opacity-40 hover:bg-cnp-bg"><ChevronLeft size={14} /></button>
              <span className="px-2 tnum">Page {page} / {totalPages}</span>
              <button disabled={page === totalPages} onClick={() => setPage((p) => p + 1)} className="p-1.5 rounded border border-cnp-border disabled:opacity-40 hover:bg-cnp-bg"><ChevronRight size={14} /></button>
            </div>
          </div>
          </>
          )}
        </SectionCard>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="by-stream-view">
          {STREAM_META.map((s) => {
            const rows = filtered.filter((r) => r.streams.includes(s.key)).sort((a, b) => (a.score || 0) - (b.score || 0));
            const Icon = s.icon;
            return (
              <SectionCard key={s.key} testid={`stream-group-${s.key}`}
                title={<span className="flex items-center gap-2" style={{ color: STREAM_COLORS[s.key] }}><Icon size={15} /> {s.label} · {rows.length}</span>}>
                {rows.length === 0 ? <EmptyState title="No applications" /> : (
                  <div className="space-y-1.5 max-h-[360px] overflow-y-auto pr-1">
                    {rows.map((r) => (
                      <div key={r.application_id} onClick={() => openExplain({ type: "application", id: r.application_id, name: r.name, title: r.name })}
                        className="flex items-center gap-2 rounded-md border border-cnp-border px-2.5 py-1.5 hover:bg-cnp-bg cursor-pointer">
                        <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: statusMeta(r.status).color }} />
                        <span className="text-xs font-medium text-cnp-text flex-1 truncate">{r.name}</span>
                        {r.streams.length > 1 && <span className="text-[9px] text-cnp-textMuted">+{r.streams.length - 1}</span>}
                        <span className="text-[10px] font-semibold px-1.5 rounded" style={{ color: CRIT_COLORS[r.criticality] }}>{r.criticality}</span>
                        <span className="tnum text-xs font-semibold w-9 text-right" style={{ color: statusMeta(r.status).color }}>{r.score == null ? "N/A" : `${r.score}%`}</span>
                      </div>
                    ))}
                  </div>
                )}
              </SectionCard>
            );
          })}
        </div>
      )}
    </div>
  );
}
